import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Search, Sparkles } from "lucide-react";
import { toast } from "sonner";
import consoleApi from "@/lib/consoleApi";
import { StatusPill } from "@/components/console/StatusPill";
import { UI_PACK_CLASSIC, UI_PACK_OPTIONS, uiPackLabel } from "@/lib/uiPack";

export default function ConsoleCompanies() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    name: "",
    slug: "",
    contact_email: "",
    owner_name: "",
    owner_email: "",
    owner_password: "",
    ui_pack: UI_PACK_CLASSIC,
  });

  const params = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      per_page: 50,
    }),
    [search, status],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["console-companies", params],
    queryFn: async () => {
      const { data: res } = await consoleApi.get("/console/companies", { params });
      return res;
    },
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        contact_email: form.contact_email.trim() || undefined,
        ui_pack: form.ui_pack || UI_PACK_CLASSIC,
      };
      if (form.owner_email.trim() && form.owner_password) {
        payload.owner = {
          name: form.owner_name.trim() || "Owner",
          email: form.owner_email.trim(),
          password: form.owner_password,
        };
      }
      const { data: res } = await consoleApi.post("/console/companies", payload);
      return res;
    },
    onSuccess: () => {
      toast.success("Company created");
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
      queryClient.invalidateQueries({ queryKey: ["console-dashboard"] });
      setShowCreate(false);
      setForm({
        name: "",
        slug: "",
        contact_email: "",
        owner_name: "",
        owner_email: "",
        owner_password: "",
        ui_pack: UI_PACK_CLASSIC,
      });
    },
    onError: (err) => {
      const msg =
        Object.values(err.response?.data?.errors || {}).flat()?.[0] ||
        err.response?.data?.message ||
        "Create failed";
      toast.error(msg);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }) => consoleApi.patch(`/console/companies/${id}/status`, { status: next }),
    onSuccess: () => {
      toast.success("Status updated");
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
      queryClient.invalidateQueries({ queryKey: ["console-dashboard"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "Status update failed"),
  });

  const uiPackMutation = useMutation({
    mutationFn: ({ id, ui_pack }) => consoleApi.patch(`/console/companies/${id}/ui-pack`, { ui_pack }),
    onSuccess: (res) => {
      toast.success(res?.data?.message || "UI pack updated — restaurant users should re-login");
      queryClient.invalidateQueries({ queryKey: ["console-companies"] });
    },
    onError: (err) => toast.error(err.response?.data?.message || "UI pack update failed"),
  });

  const companies = data?.companies || [];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="console-display text-3xl font-bold tracking-tight text-white">Companies</h1>
          <p className="mt-1 text-[14px] text-slate-400">Clients, license status, and feature unlocks</p>
        </div>
        <button type="button" className="console-btn console-btn-primary" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4" />
          Register restaurant
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            className="console-input pl-9"
            placeholder="Search name, slug, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="console-input w-40"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="blocked">Blocked</option>
        </select>
      </div>

      <div className="console-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16 text-slate-500">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : companies.length === 0 ? (
          <div className="px-5 py-14 text-center text-[13px] text-slate-500">No companies match</div>
        ) : (
          <table className="w-full text-left text-[13px]">
            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
              <tr className="border-b border-white/5">
                <th className="px-5 py-3 font-medium">Company</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">UI pack</th>
                <th className="px-5 py-3 font-medium">Registered</th>
                <th className="px-5 py-3 font-medium">Users</th>
                <th className="px-5 py-3 font-medium">Online</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((c) => (
                <tr key={c.id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                  <td className="px-5 py-3">
                    <Link
                      to={`/console/companies/${c.id}`}
                      className="font-medium text-teal-300 hover:text-teal-200 hover:underline"
                    >
                      {c.name}
                    </Link>
                    <div className="text-[11px] text-slate-500">{c.slug}</div>
                  </td>
                  <td className="px-5 py-3">
                    <StatusPill status={c.status} />
                  </td>
                  <td className="px-5 py-3">
                    <select
                      className="console-input h-8 min-w-[140px] py-0 text-[12px]"
                      value={c.ui_pack || UI_PACK_CLASSIC}
                      disabled={uiPackMutation.isPending}
                      onChange={(e) => {
                        const next = e.target.value;
                        if (next === (c.ui_pack || UI_PACK_CLASSIC)) return;
                        if (
                          !window.confirm(
                            `Switch ${c.name} to ${uiPackLabel(next)}?\n\nStaff must log out and log in again.`,
                          )
                        ) {
                          e.target.value = c.ui_pack || UI_PACK_CLASSIC;
                          return;
                        }
                        uiPackMutation.mutate({ id: c.id, ui_pack: next });
                      }}
                      data-testid={`console-list-ui-pack-${c.id}`}
                      title="Restaurant UI pack"
                    >
                      {UI_PACK_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-5 py-3 text-slate-400">
                    {c.registered_at ? new Date(c.registered_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-5 py-3 tabular-nums text-slate-300">{c.users_count ?? 0}</td>
                  <td className="px-5 py-3">
                    {c.features?.online ? (
                      <span className="text-teal-300">On</span>
                    ) : (
                      <span className="text-slate-500">Off</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex flex-wrap justify-end gap-1">
                      <Link
                        to={`/console/companies/${c.id}#features`}
                        className="console-btn console-btn-primary h-8 px-2 text-[11px]"
                      >
                        <Sparkles className="h-3 w-3" />
                        Features
                      </Link>
                      {c.status !== "active" && (
                        <button
                          type="button"
                          className="console-btn console-btn-ghost h-8 px-2 text-[11px]"
                          onClick={() => statusMutation.mutate({ id: c.id, next: "active" })}
                        >
                          Activate
                        </button>
                      )}
                      {c.status !== "suspended" && (
                        <button
                          type="button"
                          className="console-btn console-btn-ghost h-8 px-2 text-[11px]"
                          onClick={() => statusMutation.mutate({ id: c.id, next: "suspended" })}
                        >
                          Suspend
                        </button>
                      )}
                      {c.status !== "blocked" && (
                        <button
                          type="button"
                          className="console-btn console-btn-ghost h-8 px-2 text-[11px] text-rose-300"
                          onClick={() => statusMutation.mutate({ id: c.id, next: "blocked" })}
                        >
                          Block
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-sm">
          <div className="console-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-6">
            <h2 className="console-display mb-1 text-xl font-bold text-white">Register restaurant</h2>
            <p className="mb-4 text-[12px] text-slate-500">
              Create the company and choose which UI their staff will use after login.
            </p>
            <div className="space-y-3">
              {[
                ["name", "Restaurant / company name *", "text"],
                ["slug", "Slug (optional)", "text"],
                ["contact_email", "Contact email", "email"],
                ["owner_name", "Owner name", "text"],
                ["owner_email", "Owner login email", "email"],
                ["owner_password", "Owner password", "password"],
              ].map(([key, label, type]) => (
                <label key={key} className="block">
                  <span className="mb-1 block text-[12px] text-slate-400">{label}</span>
                  <input
                    className="console-input"
                    type={type}
                    value={form[key]}
                    onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                    required={key === "name"}
                  />
                </label>
              ))}

              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="console-create-ui-pack">
                <div className="mb-2">
                  <span className="block text-[12px] font-semibold text-white">Restaurant UI *</span>
                  <span className="mt-0.5 block text-[11px] text-slate-500">
                    Required — Classic keeps the current ERP; Simple is the new touch UI for all staff.
                  </span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {UI_PACK_OPTIONS.map((opt) => {
                    const active = form.ui_pack === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, ui_pack: opt.value }))}
                        className={
                          active
                            ? "rounded-lg border border-teal-400/50 bg-teal-500/15 p-3 text-start"
                            : "rounded-lg border border-white/10 bg-black/20 p-3 text-start hover:border-teal-400/30"
                        }
                        data-testid={`console-create-ui-${opt.value}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[13px] font-bold text-white">{opt.label}</span>
                          {active ? (
                            <span className="text-[10px] font-bold uppercase tracking-wide text-teal-300">Selected</span>
                          ) : null}
                        </div>
                        <p className="mt-1 text-[11px] leading-snug text-slate-400">{opt.hint}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="console-btn console-btn-ghost" onClick={() => setShowCreate(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="console-btn console-btn-primary"
                disabled={createMutation.isPending || !form.name.trim() || !form.ui_pack}
                onClick={() => createMutation.mutate()}
                data-testid="console-create-company"
              >
                {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Register
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
